import React, { useState, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import { fetchProject, fetchComments, addComment, fetchSettings } from '../services/db';

const formatTime = (ts) => {
  if (!ts?.seconds) return 'Gerade eben';
  return new Date(ts.seconds * 1000).toLocaleString('de-DE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

export default function ClientShareView({ shareToken }) {
  const [project, setProject]     = useState(null);
  const [comments, setComments]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [notFound, setNotFound]   = useState(false);
  const [form, setForm]           = useState({ text: '', timestamp: '', type: 'general', name: '' });
  const [sending, setSending]     = useState(false);
  const [sent, setSent]           = useState(false);
  const [brandColor, setBrandColor] = useState('#6366f1');

  const loadData = useCallback(async () => {
    try {
      const p = await fetchProject(shareToken);
      if (!p) { setNotFound(true); setLoading(false); return; }
      setProject(p);
      const [c, s] = await Promise.all([fetchComments(shareToken), fetchSettings(p.ownerId)]);
      setComments(c);
      if (s.brandColor) setBrandColor(s.brandColor);
    } catch (e) {
      console.error(e);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [shareToken]);

  useEffect(() => { loadData(); }, [loadData]);

  // Poll for new comments every 10s
  useEffect(() => {
    if (!project) return;
    const interval = setInterval(async () => {
      try {
        const c = await fetchComments(shareToken);
        setComments(c);
      } catch (_) {}
    }, 10000);
    return () => clearInterval(interval);
  }, [shareToken, project]);

  const handleSend = async () => {
    if (!form.text.trim()) return;
    setSending(true);
    try {
      await addComment(shareToken, {
        text: form.text.trim(),
        name: form.name.trim() || 'Kunde',
        timestamp: form.type === 'timestamp' ? form.timestamp : null,
        type: form.type,
        source: 'client',
      });
      setForm(f => ({ ...f, text: '', timestamp: '' }));
      setSent(true);
      setTimeout(() => setSent(false), 2500);
      const c = await fetchComments(shareToken);
      setComments(c);
    } catch (e) {
      console.error(e);
    } finally {
      setSending(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <svg className="animate-spin w-5 h-5 text-gray-300" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
      </svg>
    </div>
  );

  if (notFound) return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center text-center px-6">
      <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M4 3L18 11L4 19V3Z" stroke="#d1d5db" strokeWidth="1.5"/></svg>
      </div>
      <p className="text-gray-500 font-medium mb-1">Projekt nicht gefunden</p>
      <p className="text-sm text-gray-400">Dieser Link ist ungültig oder wurde gelöscht.</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-100 px-6 flex items-center justify-between h-14 sticky top-0 z-50">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ backgroundColor: brandColor }}>
            <svg width="11" height="11" viewBox="0 0 14 14" fill="none"><path d="M2 1.5L12 7L2 12.5V1.5Z" fill="white"/></svg>
          </div>
          <span className="text-sm font-semibold text-gray-900 tracking-tight">FrameIt</span>
        </div>
        <span className="text-xs text-gray-400 font-medium">Kunden-Ansicht</span>
        <div className="w-20" />
      </header>

      <div className="max-w-6xl mx-auto flex flex-col md:flex-row gap-0 md:gap-8 px-0 md:px-6 py-0 md:py-8">
        {/* LEFT: Content */}
        <div className="flex-1 bg-white md:rounded-2xl md:border md:border-gray-100 overflow-hidden">
          <div className="max-w-2xl mx-auto px-6 md:px-10 py-8 md:py-10">
            <div className="mb-8">
              <p className="text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: brandColor }}>Dein Video-Konzept</p>
              <h1 className="text-2xl font-semibold text-gray-900 leading-tight">{project.title}</h1>
            </div>

            {project.driveLink && (
              <div className="mb-8 rounded-2xl overflow-hidden border border-gray-100">
                <div className="aspect-video bg-gray-900 flex flex-col items-center justify-center">
                  <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center mb-3">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M5 3L19 12L5 21V3Z" fill="white"/></svg>
                  </div>
                  <p className="text-white/50 text-sm mb-3">Fertiges Video</p>
                  <a href={project.driveLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-white text-sm font-medium px-4 py-2 rounded-xl transition-all" style={{ backgroundColor: brandColor }}>
                    <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M4 3L12 8L4 13V3Z" fill="white"/></svg>
                    Im Drive öffnen
                  </a>
                </div>
              </div>
            )}

            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-5">Skript & Konzept</p>
              <div className="flex flex-col gap-6">
                {project.blocks?.map(block => (
                  <div key={block.id}>
                    {block.type === 'text' && block.content && (
                      <div className="prose-custom"><ReactMarkdown>{block.content}</ReactMarkdown></div>
                    )}
                    {block.type === 'image' && block.imageUrl && (
                      <div className="rounded-2xl overflow-hidden border border-gray-100">
                        <img src={block.imageUrl} alt={block.imageCaption || ''} className="w-full object-cover" />
                        {block.imageCaption && <p className="text-xs text-gray-400 px-4 py-2 bg-gray-50">{block.imageCaption}</p>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT: Feedback */}
        <div className="w-full md:w-80 md:shrink-0 flex flex-col gap-4">
          {/* Comment form */}
          <div className="bg-white md:rounded-2xl md:border md:border-gray-100 p-5">
            <p className="text-sm font-semibold text-gray-800 mb-0.5">Feedback hinterlassen</p>
            <p className="text-xs text-gray-400 mb-4">Schreib uns deine Anmerkungen zum Video.</p>
            <div className="mb-3">
              <label className="text-xs font-medium text-gray-500 block mb-1.5">Dein Name (optional)</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="z. B. Anna Müller" className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2.5 outline-none focus:border-indigo-300 focus:ring-2 focus:ring-indigo-50 transition-all" />
            </div>
            <div className="flex gap-1.5 mb-3">
              <button onClick={() => setForm(f => ({ ...f, type: 'general' }))} className={`flex-1 text-xs py-2 rounded-xl font-medium transition-all ${form.type === 'general' ? 'text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`} style={form.type === 'general' ? { backgroundColor: brandColor } : {}}>Allgemein</button>
              <button onClick={() => setForm(f => ({ ...f, type: 'timestamp' }))} className={`flex-1 text-xs py-2 rounded-xl font-medium transition-all ${form.type === 'timestamp' ? 'text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`} style={form.type === 'timestamp' ? { backgroundColor: brandColor } : {}}>Bei Sekunde…</button>
            </div>
            {form.type === 'timestamp' && (
              <input value={form.timestamp} onChange={e => setForm(f => ({ ...f, timestamp: e.target.value }))} placeholder="z. B. 00:42" className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2.5 outline-none focus:border-indigo-300 mb-3 font-mono" />
            )}
            <textarea value={form.text} onChange={e => setForm(f => ({ ...f, text: e.target.value }))} onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSend(); }} placeholder="Deine Anmerkung…" rows={4} className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2.5 outline-none focus:border-indigo-300 focus:ring-2 focus:ring-indigo-50 transition-all resize-none leading-relaxed mb-3" />
            <button onClick={handleSend} disabled={!form.text.trim() || sending} className="w-full py-2.5 text-white text-sm font-medium rounded-xl disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2" style={{ backgroundColor: brandColor }}>
              {sending && <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>}
              {sent ? 'Gesendet ✓' : 'Anmerkung senden'}
            </button>
          </div>

          {/* Comments list */}
          {comments.length > 0 && (
            <div className="bg-white md:rounded-2xl md:border md:border-gray-100 p-5">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">Bisheriges Feedback ({comments.length})</p>
              <div className="flex flex-col gap-3 max-h-96 overflow-y-auto pr-1">
                {comments.map(c => (
                  <div key={c.id} className={`rounded-xl p-3.5 border ${c.source === 'client' ? 'border-red-100 bg-red-50/50' : 'border-gray-100 bg-gray-50'}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-1.5">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-semibold ${c.source === 'client' ? 'bg-red-100 text-red-600' : 'bg-indigo-100 text-indigo-600'}`}>
                          {(c.name || c.author || 'K')[0].toUpperCase()}
                        </div>
                        <span className={`text-xs font-medium ${c.source === 'client' ? 'text-red-700' : 'text-gray-700'}`}>{c.name || c.author || 'Kunde'}</span>
                      </div>
                      {c.timestamp && <span className={`text-xs font-mono px-1.5 py-0.5 rounded ${c.source === 'client' ? 'bg-red-100 text-red-600' : 'bg-indigo-50 text-indigo-500'}`}>{c.timestamp}</span>}
                    </div>
                    <p className={`text-sm leading-relaxed ${c.source === 'client' ? 'text-red-800' : 'text-gray-700'}`}>{c.text}</p>
                    <p className="text-xs text-gray-400 mt-1">{formatTime(c.createdAt)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
